import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Package, User, ChevronRight, Loader2, Users } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { user, getAuthHeaders, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: getAuthHeaders()
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const registerAsAffiliate = async () => {
    setRegistering(true);
    try {
      await axios.post(`${API}/affiliate/register`, {}, {
        headers: getAuthHeaders()
      });
      await fetchUser();
      toast.success("You're now an affiliate! Check your dashboard for your referral link.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register as affiliate");
    } finally {
      setRegistering(false);
    }
  };

  if (!user) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "delivered":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  return (
    <div className="container-main section-spacing">
      <h1 className="text-h2 mb-8">My Account</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-xl">{user.name}</h2>
                <p className="text-muted-foreground text-sm">{user.email}</p>
              </div>
            </div>

            {user.is_affiliate ? (
              <Link to="/affiliate">
                <Button variant="outline" className="w-full mb-4" data-testid="affiliate-dashboard-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Affiliate Dashboard
                </Button>
              </Link>
            ) : (
              <Button 
                variant="outline" 
                className="w-full mb-4"
                onClick={registerAsAffiliate}
                disabled={registering}
                data-testid="become-affiliate-btn"
              >
                {registering ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Users className="w-4 h-4 mr-2" />
                )}
                Become an Affiliate
              </Button>
            )}

            {user.is_admin && (
              <Link to="/admin">
                <Button variant="outline" className="w-full" data-testid="admin-dashboard-btn">
                  Admin Dashboard
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Orders */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-border rounded-xl p-6">
            <h2 className="font-heading text-xl mb-6 flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Order History
            </h2>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse p-4 bg-muted/30 rounded-lg">
                    <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No orders yet</p>
                <Link to="/#products">
                  <Button className="btn-primary" data-testid="start-shopping-btn">
                    Start Shopping
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div 
                    key={order.id} 
                    className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">Order #{order.id.slice(0, 8)}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        <p className="text-primary font-medium mt-1">${order.total.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{order.items.length} item{order.items.length > 1 ? "s" : ""}</span>
                      <ChevronRight className="w-4 h-4" />
                      <span className="truncate">
                        {order.items.map(i => i.name).join(", ")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
